"use client";

import React, { Suspense, useState } from 'react';
import { useSearchParams } from 'next/navigation';

/**
 * Appeal submission.
 *
 * FIX (2026-08-30): this page used to be ten lines — a heading and a paragraph
 * telling a flagged wallet owner to "submit an appeal with evidence." There was
 * no form, no link, and nothing anywhere in the application posted to
 * /api/appeal-wallet. That endpoint exists, validates its input against
 * appealWalletSchema, rate-limits to 3/hour and writes an Appeal document; and
 * /admin/appeals exists to review a queue that could therefore never receive
 * anything. The whole feature was unreachable from the user side.
 *
 * The form below is the missing half. Field names match appealWalletSchema
 * exactly (chain, address, contactEmail, evidence[]), and the wallet profile
 * links here with ?chain=&address= prefilled so an owner disputing a flag
 * arrives with the wallet already identified.
 */

const CHAINS = [
  { value: 'ethereum', label: 'Ethereum' },
  { value: 'bsc', label: 'BNB Smart Chain' },
  { value: 'polygon', label: 'Polygon' },
  { value: 'arbitrum', label: 'Arbitrum' },
  { value: 'optimism', label: 'Optimism' },
  { value: 'base', label: 'Base' },
  { value: 'avalanche', label: 'Avalanche' },
  { value: 'bitcoin', label: 'Bitcoin' },
  { value: 'solana', label: 'Solana' },
  { value: 'tron', label: 'Tron' },
  { value: 'xrp', label: 'XRP' },
];

const MAX_EVIDENCE = 5;

function AppealForm() {
  const params = useSearchParams();
  const readParam = (k: string) => {
    try {
      return params?.get(k) ?? '';
    } catch {
      return '';
    }
  };

  const [chain, setChain] = useState(readParam('chain') || 'ethereum');
  const [address, setAddress] = useState(readParam('address'));
  const [contactEmail, setContactEmail] = useState('');
  const [evidence, setEvidence] = useState<string[]>(['']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const setLine = (i: number, v: string) =>
    setEvidence((prev) => prev.map((x, n) => (n === i ? v : x)));
  const addLine = () =>
    setEvidence((prev) => (prev.length >= MAX_EVIDENCE ? prev : [...prev, '']));
  const removeLine = (i: number) =>
    setEvidence((prev) => (prev.length === 1 ? [''] : prev.filter((_, n) => n !== i)));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);

    const addr = address.trim();
    if (!addr) {
      setError('Enter the wallet address you are appealing for.');
      return;
    }
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) {
      setError('That email address does not look valid. Leave it blank if you would rather not give one.');
      return;
    }

    setBusy(true);
    try {
      const lines = evidence.map((x) => x.trim()).filter(Boolean).slice(0, MAX_EVIDENCE);
      const body: Record<string, unknown> = { chain, address: addr };
      // Both are optional in the schema; only send them when present rather
      // than sending empty strings, which `.email()` and `.min(1)` reject.
      if (contactEmail.trim()) body.contactEmail = contactEmail.trim();
      if (lines.length) body.evidence = lines;

      const res = await fetch('/api/appeal-wallet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const json = (await res.json().catch(() => null)) as
        | { ok?: boolean; id?: string; error?: string; details?: Record<string, unknown> }
        | null;

      if (res.status === 429) {
        setError('You have submitted several appeals recently. Please try again in an hour.');
        return;
      }
      if (!res.ok) {
        // Name the rejected fields rather than showing a bare "Invalid input".
        const d = json?.details;
        const fields = d ? Object.keys(d).filter((k) => k !== '_errors') : [];
        setError((json?.error || `Request failed (${res.status})`) + (fields.length ? ` — check: ${fields.join(', ')}` : ''));
        return;
      }
      setDone(json?.id || 'received');
    } catch {
      setError('Could not reach the appeals service. Please check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <section className="pr-panel" aria-live="polite">
        <p className="pr-panel-title">Appeal received</p>
        <p className="pr-hint mt-2">
          Your appeal is queued for moderator review. Existing flags stay in place until a
          moderator decides — submitting an appeal does not remove them automatically.
        </p>
        {done !== 'received' && (
          <p className="pr-hint mt-3">
            Reference: <span className="font-mono">{done}</span> — keep this if you need to follow up.
          </p>
        )}
        <div className="pr-actions mt-5">
          <a href={`/wallet/${encodeURIComponent(chain)}/${encodeURIComponent(address)}`} className="pr-btn pr-btn-primary pr-btn-sm">
            Back to the wallet
          </a>
          <a href="/search" className="pr-btn pr-btn-ghost pr-btn-sm">Search another wallet</a>
        </div>
      </section>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <section className="pr-panel">
        <p className="pr-panel-title">Submit an appeal</p>
        <p className="pr-hint mt-2">
          Tell us which wallet is affected and why the flags are wrong. Evidence links help a
          moderator resolve it faster.
        </p>

        <div className="mt-5 grid gap-4 sm:grid-cols-[minmax(0,13rem)_minmax(0,1fr)]">
          <div>
            <label className="pr-label" htmlFor="ap-chain">Chain</label>
            <select id="ap-chain" className="pr-select" value={chain} onChange={(e) => setChain(e.target.value)}>
              {CHAINS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="pr-label" htmlFor="ap-address">Wallet address</label>
            <input
              id="ap-address"
              className="pr-input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="The address the flags are against"
              autoComplete="off"
              spellCheck={false}
              required
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="pr-label" htmlFor="ap-email">
            Contact email <span className="opacity-60">(optional)</span>
          </label>
          <input
            id="ap-email"
            type="email"
            className="pr-input"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="So a moderator can reach you about the outcome"
            autoComplete="email"
          />
        </div>

        <fieldset className="mt-5" style={{ border: 0, margin: 0, padding: 0 }}>
          <legend className="pr-label" style={{ padding: 0 }}>
            Evidence <span className="opacity-60">(optional, up to {MAX_EVIDENCE})</span>
          </legend>
          <p className="pr-hint mt-1">
            Transaction hashes, an explorer link, a support ticket — anything that shows the
            activity was legitimate.
          </p>
          <div className="mt-3 grid gap-2">
            {evidence.map((line, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <input
                  className="pr-input"
                  style={{ flex: '1 1 16rem' }}
                  value={line}
                  onChange={(e) => setLine(i, e.target.value)}
                  placeholder={i === 0 ? 'https://etherscan.io/tx/0x…' : 'Another link or reference'}
                  aria-label={`Evidence ${i + 1}`}
                  maxLength={500}
                />
                <button
                  type="button"
                  className="pr-btn pr-btn-ghost pr-btn-sm"
                  onClick={() => removeLine(i)}
                  aria-label={`Remove evidence ${i + 1}`}
                  disabled={evidence.length === 1 && !line}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          {evidence.length < MAX_EVIDENCE && (
            <div className="pr-actions mt-3">
              <button type="button" className="pr-btn pr-btn-ghost pr-btn-sm" onClick={addLine}>
                + Add another
              </button>
            </div>
          )}
        </fieldset>

        {error && (
          <p className="pr-error mt-4" role="alert">{error}</p>
        )}

        <div className="pr-actions mt-5">
          <button type="submit" className="pr-btn pr-btn-primary" disabled={busy} aria-busy={busy}>
            {busy ? 'Submitting…' : 'Submit appeal'}
          </button>
        </div>
        <p className="pr-hint mt-3">
          Appeals are limited to three per hour. Submitting one does not remove existing flags —
          a moderator reviews each case.
        </p>
      </section>
    </form>
  );
}

export default function AppealProcess() {
  return (
    <div className="min-h-screen">
      <main className="mx-auto w-full max-w-3xl px-4 pb-14 pt-24 sm:px-6 sm:pt-28">
        <header className="mb-6">
          <p className="pr-section-sub">Trust operations</p>
          <h1 className="pr-section-title mt-2">Appeal a flag</h1>
          <p className="pr-hint mt-3">
            If you believe a wallet has been flagged incorrectly, submit an appeal with evidence.
            Appeals are stored and reviewed by our moderation team.
          </p>
        </header>
        {/* useSearchParams needs a Suspense boundary or the whole route opts
            out of static rendering. */}
        <Suspense fallback={<section className="pr-panel"><p className="pr-hint">Loading…</p></section>}>
          <AppealForm />
        </Suspense>
      </main>
    </div>
  );
}
